/* ============================================================
   Eureka giving block behaviour.

   WHY THIS IS A SEPARATE ASSET AND NOT AN INLINE <script>

   snippets/cart-drawer.liquid only renders cart-summary (and therefore the
   giving block) when the cart is NOT empty:

       {%- if cart.empty? -%}  empty state  {%- else -%}  cart-summary  {%- endif -%}

   So on a product page loaded with an empty cart, the giving block never
   renders and its inline <script> never exists. Add a disc and Shopify
   re-renders the drawer and injects the new HTML -- which contains the
   <script> tag, but a <script> inserted by parsing HTML into the DOM is
   NEVER executed. That is a DOM rule, not a bug we could work around with
   better timing.

   Result: no click handler at all, so picking a charity did nothing until
   you reloaded the page -- at which point the cart was non-empty, the block
   was in the INITIAL html, and the browser ran the script normally. Exactly
   the "only works after a refresh" symptom.

   Loaded from layout/theme.liquid, this file runs on every page load no
   matter what the cart contains. Two mechanisms, both immune to re-renders:

     1. Clicks are handled by ONE delegated listener on the document, so it
        does not matter how many times the drawer is replaced or morphed --
        there is no per-element listener to lose.
     2. <eureka-giving> is registered as a custom element, so
        connectedCallback fires automatically every time one is inserted,
        however it is inserted. That is what runs the one-time cart sync.
   ============================================================ */
(() => {
  if (window.__eurekaGivingLoaded) return;
  window.__eurekaGivingLoaded = true;

  const CARD = '.egiving__card--pick';

  /* Always send BOTH attributes. Passing an empty string is how Shopify
     clears an attribute, and clearing Nonprofit is meaningful here: blank is
     the signal Give & Grow uses to run its own even split. Sending only the
     keys that happen to be truthy would leave a stale EIN behind when a
     customer clears their pick. */
  const write = (cause, ein) =>
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { Cause: cause || '', Nonprofit: ein || '' } })
    }).catch(() => {});

  const setField = (root, name, value) => {
    const el = root.querySelector('input[name="attributes[' + name + ']"]');
    if (el) el.value = value;
  };

  const paint = (root, cause) => {
    root.querySelectorAll(CARD).forEach((el) => {
      const on = !!cause && el.dataset.cause === cause;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    /* the "no pick, split evenly" line follows the live state, so it
       reappears when someone toggles their pick back off */
    const note = root.querySelector('[data-egiving-default-note]');
    if (note) note.hidden = !!cause;
  };

  /* ---------- clicks: one listener, for the life of the page ---------- */
  document.addEventListener('click', (e) => {
    const card = e.target.closest(CARD);
    if (!card) return;
    const root = card.closest('eureka-giving');
    if (!root) return;
    e.preventDefault();

    /* Toggle, not just select: pressing the card that is already active
       clears the pick and returns to the even split. */
    const already = card.classList.contains('is-selected');
    const cause = already ? '' : (card.dataset.cause || '');
    const ein = already ? '' : (card.dataset.ein || '');

    paint(root, cause);
    setField(root, 'Cause', cause || (root.dataset.defaultCause || ''));
    setField(root, 'Nonprofit', ein);
    write(cause || (root.dataset.defaultCause || ''), ein);
  });

  /* keyboard parity: space and enter on a focused card */
  document.addEventListener('keydown', (e) => {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    const card = e.target.closest && e.target.closest(CARD);
    if (!card) return;
    e.preventDefault();
    card.click();
  });

  /* ---------- one-time cart sync per inserted block ---------- */
  const syncSpotlight = (root) => {
    const want = root.dataset.spotlightCause || '';
    const ein = root.dataset.spotlightEin || '';
    if (!want) return;
    fetch('/cart.js')
      .then((r) => r.json())
      .then((c) => {
        if (((c.attributes && c.attributes.Cause) || '') === want) return;
        setField(root, 'Cause', want);
        if (ein) setField(root, 'Nonprofit', ein);
        write(want, ein);
      })
      .catch(() => {});
  };

  /* If nobody has picked yet, write a real describable default rather than
     leaving Cause blank, so the confirmation email always has something
     honest to show. Nonprofit stays blank on purpose. */
  const syncChoiceDefault = (root) => {
    const fallback = root.dataset.defaultCause || '';
    if (!fallback) return;
    fetch('/cart.js')
      .then((r) => r.json())
      .then((c) => {
        if ((c.attributes && c.attributes.Cause) || '') return;
        setField(root, 'Cause', fallback);
        write(fallback, '');
      })
      .catch(() => {});
  };

  class EurekaGiving extends HTMLElement {
    connectedCallback() {
      /* guard the SYNC only. The click handler is delegated, so it needs no
         guarding and cannot be lost. */
      if (this.dataset.egSynced) return;
      this.dataset.egSynced = '1';
      if (this.dataset.mode === 'spotlight') syncSpotlight(this);
      else syncChoiceDefault(this);
    }
  }

  if (!customElements.get('eureka-giving')) {
    customElements.define('eureka-giving', EurekaGiving);
  }
})();
